import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarClock, CheckCircle2, MailWarning, Package, RefreshCcw, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import AdminPanel from '../components/dashboard/AdminPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtCad = (n) => `CA$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '');

const SubscriptionCard = ({ sub, onCancelled, t }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const cancel = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/dashboard/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subscription_id: sub.subscription_id }),
      });
      if (!res.ok) throw new Error();
      onCancelled(sub.subscription_id);
    } catch {
      setError('Could not cancel. Please try again.');
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const cancelled = sub.cancel_at_period_end || ['canceled', 'cancelled'].includes(sub.status);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="subscription-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <RefreshCcw size={15} style={{ color: '#e2c179' }} />
            <span
              className="text-[11px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full"
              style={cancelled
                ? { background: 'rgba(239,68,68,0.12)', color: '#fca5a5' }
                : { background: 'rgba(74,222,128,0.12)', color: '#4ade80' }}
              data-testid="subscription-status"
            >
              {cancelled ? t('dbCancelScheduled') : t('dbActive')}
            </span>
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
            {fmtCad(sub.monthly_total)}<span className="text-sm text-gray-500 font-normal">/mo</span>
          </p>
        </div>
        {sub.current_period_end && (
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wide text-gray-600 mb-1">{t('dbRenews')}</p>
            <p className="text-sm text-gray-300 flex items-center gap-1.5"><CalendarClock size={13} />{fmtDate(sub.current_period_end)}</p>
          </div>
        )}
      </div>
      <ul className="space-y-1.5 mb-5">
        {(sub.items || []).map((item, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
            <CheckCircle2 size={13} style={{ color: '#e2c179' }} className="shrink-0" />
            {item}
          </li>
        ))}
      </ul>
      {!cancelled && (
        confirming ? (
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <p className="text-xs text-gray-400 flex-1">{t('dbCancelConfirm')}</p>
            <div className="flex gap-2">
              <button
                className="text-xs font-semibold px-4 py-2 rounded-full border border-white/15 text-gray-300 hover:bg-white/10 transition-colors"
                onClick={() => setConfirming(false)}
                data-testid="cancel-sub-keep-btn"
              >
                {t('dbCancelKeep')}
              </button>
              <button
                className="text-xs font-semibold px-4 py-2 rounded-full transition-colors"
                style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)' }}
                onClick={cancel}
                disabled={busy}
                data-testid="cancel-sub-confirm-btn"
              >
                {busy ? '...' : t('dbCancelYes')}
              </button>
            </div>
          </div>
        ) : (
          <button
            className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-red-300 transition-colors"
            onClick={() => setConfirming(true)}
            data-testid="cancel-sub-btn"
          >
            <XCircle size={13} />
            {t('dbCancelSub')}
          </button>
        )
      )}
      {error && <p className="mt-3 text-xs" style={{ color: '#fca5a5' }}>{error}</p>}
    </div>
  );
};

const OrderCard = ({ order, t }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="order-card">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(226,193,121,0.12)' }}>
          <Package size={18} style={{ color: '#e2c179' }} />
        </div>
        <div>
          <p className="text-base font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="order-package">
            {order.package || 'Custom Project'}
          </p>
          <p className="text-xs text-gray-500">{t('dbOrderDate')} {fmtDate(order.date)}</p>
        </div>
      </div>
      <span className="text-[11px] font-bold tracking-wide uppercase px-2.5 py-1 rounded-full" style={{ background: 'rgba(74,222,128,0.12)', color: '#4ade80' }}>
        {t('dbStatusPaid')}
      </span>
    </div>
    {order.addons.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mb-4">
        {order.addons.map((a, i) => (
          <span key={i} className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-gray-400">{a}</span>
        ))}
      </div>
    )}
    <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/[0.07]">
      <div>
        <p className="text-[10px] uppercase tracking-wide text-gray-600 mb-0.5">{t('dbDeposit')}</p>
        <p className="text-sm font-semibold text-white">{fmtCad(order.deposit)}</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-gray-600 mb-0.5">{t('dbBalance')}</p>
        <p className="text-sm font-semibold text-white">{fmtCad(order.one_time_total - order.deposit)}</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-gray-600 mb-0.5">{t('dbMonthlyLabel')}</p>
        <p className="text-sm font-semibold text-white">{order.monthly_total > 0 ? `${fmtCad(order.monthly_total)}/mo` : '-'}</p>
      </div>
    </div>
  </div>
);

const ClientDashboard = ({ user }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/dashboard`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch {}
      setLoading(false);
    })();
  }, []);

  const markCancelled = (sid) => {
    setData((d) => ({
      ...d,
      subscriptions: d.subscriptions.map((s) => (s.subscription_id === sid ? { ...s, cancel_at_period_end: true } : s)),
    }));
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-8 pt-32 md:pt-40 pb-24" data-testid="client-dashboard">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="accent-tag">{t('dbTag')}</p>
        <div className="flex items-center gap-4 mb-14">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-white/15" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-black text-lg font-bold" style={{ background: 'linear-gradient(135deg, #706958, #e2c179)' }}>
              {(user.name || user.email || '?')[0].toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="dashboard-greeting">
              {t('dbWelcome')}, {(user.name || '').split(' ')[0] || user.email}
            </h1>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>
      </motion.div>

      {!user.email_verified && (
        <div
          className="flex items-start gap-3 rounded-xl border p-4 mb-10 -mt-6"
          style={{ borderColor: 'rgba(226,193,121,0.35)', background: 'rgba(226,193,121,0.08)' }}
          data-testid="verify-email-banner"
        >
          <MailWarning size={16} style={{ color: '#e2c179' }} className="mt-0.5 shrink-0" />
          <p className="text-sm" style={{ color: '#e2c179' }}>{t('authVerifyBanner')}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="relative w-10 h-10">
            <div className="absolute inset-0 rounded-full border-2 border-white/10" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#e2c179' }} />
          </div>
        </div>
      ) : (
        <div className="space-y-14">
          <section data-testid="dashboard-subscriptions">
            <h2 className="text-lg font-semibold text-white mb-5" style={{ fontFamily: "'Syne', sans-serif" }}>{t('dbSubsTitle')}</h2>
            {(data?.subscriptions || []).length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="no-subscriptions">{t('dbNoSubs')}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.subscriptions.map((s) => (
                  <SubscriptionCard key={s.subscription_id} sub={s} onCancelled={markCancelled} t={t} />
                ))}
              </div>
            )}
          </section>

          <section data-testid="dashboard-orders">
            <h2 className="text-lg font-semibold text-white mb-5" style={{ fontFamily: "'Syne', sans-serif" }}>{t('dbOrdersTitle')}</h2>
            {(data?.orders || []).length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="no-orders">
                <p className="text-sm text-gray-500 mb-6">{t('dbNoOrders')}</p>
                <button className="btn-accent" onClick={() => navigate('/services')} data-testid="dashboard-browse-btn">
                  {t('dbBrowse')}
                  <ArrowRight size={15} />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.orders.map((o, i) => <OrderCard key={i} order={o} t={t} />)}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-2 border-white/10" />
          <div className="absolute inset-0 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#e2c179' }} />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#050505' }} data-testid="dashboard-signin">
        <motion.div
          className="w-full max-w-md rounded-3xl border border-white/10 p-10 text-center"
          style={{ background: 'radial-gradient(ellipse at 50% -20%, rgba(226,193,121,0.1) 0%, #0a0a0a 60%)' }}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <img src="/cexus-logo.png" alt="CEXUS" className="w-12 h-12 mx-auto mb-6 object-contain" style={{ filter: 'brightness(1.9)' }} />
          <h1 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>{t('dbSignInTitle')}</h1>
          <p className="text-sm text-gray-400 mb-8">{t('dbSignInText')}</p>
          <button
            className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-gray-200 transition-colors"
            onClick={() => navigate('/auth', { state: { from: '/dashboard' } })}
            data-testid="dashboard-signin-btn"
          >
            {t('dbSignInBtn')}
            <ArrowRight size={15} />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ background: '#050505', minHeight: '100vh' }} data-testid="dashboard-page">
      {user.is_admin ? <AdminPanel user={user} /> : <ClientDashboard user={user} />}
    </div>
  );
}
